import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { LayoutDashboard, Package, ShoppingBag, Users, Star, BookOpen, ScrollText, Plus, Trash2, Edit, Check, X, ArrowLeft, ShieldCheck, Settings, ListPlus, QrCode, Printer, Download, Search, Bell, Eye } from "lucide-react";
import { api, fmtPrice, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const TABS = [
  { key: "overview", label: "نظرة عامة", icon: LayoutDashboard },
  { key: "products", label: "المنتجات", icon: Package },
  { key: "orders", label: "الطلبات", icon: ShoppingBag },
  { key: "users", label: "العملاء", icon: Users },
  { key: "reviews", label: "التقييمات", icon: Star },
  { key: "religious", label: "المحتوى الديني", icon: BookOpen },
  { key: "fields", label: "الحقول الديناميكية", icon: ListPlus },
  { key: "settings", label: "الإعدادات", icon: Settings },
  { key: "admins", label: "المديرون", icon: ShieldCheck, superOnly: true },
  { key: "audit", label: "سجل العمليات", icon: ScrollText },
];

export const FUL = {
  new: { label: "جديد", cls: "bg-olive/10 text-olive" },
  preparing: { label: "قيد التجهيز", cls: "bg-earth/10 text-earth" },
  ready: { label: "جاهز للتسليم", cls: "bg-gold/15 text-olive-dark" },
  delivering: { label: "قيد التوصيل", cls: "bg-blue-500/10 text-blue-700" },
  delivered: { label: "تم التسليم", cls: "bg-olive/15 text-olive-dark" },
  cancelled: { label: "ملغي", cls: "bg-palred/10 text-palred" },
};

export default function Admin() {
  const { user, ready } = useAuth();
  const [tab, setTab] = useState("overview");
  const navigate = useNavigate();

  const isStaff = user && ["admin", "super_admin", "manager"].includes(user.role);

  useEffect(() => {
    if (ready && !isStaff) navigate("/login?redirect=/admin");
  }, [ready, isStaff]);

  if (!isStaff) return <div className="flex min-h-screen items-center justify-center pt-24 text-ink/50">جارِ التحميل...</div>;

  const visibleTabs = TABS.filter((t) => !t.superOnly || user.role === "super_admin");

  return (
    <div className="min-h-screen bg-sand/20 pt-20">
      <div className="mx-auto flex max-w-7xl gap-6 px-4 py-6">
        <aside className="hidden w-56 shrink-0 lg:block">
          <div className="sticky top-24 space-y-1 rounded-3xl border border-olive/10 bg-white p-3">
            {visibleTabs.map((t) => (
              <button key={t.key} data-testid={`admin-tab-${t.key}`} onClick={() => setTab(t.key)}
                className={`flex w-full items-center gap-2.5 rounded-xl px-4 py-2.5 text-sm font-medium transition-colors ${tab === t.key ? "bg-olive text-ivory" : "text-ink/65 hover:bg-olive/5"}`}>
                <t.icon className="h-4 w-4" /> {t.label}
              </button>
            ))}
          </div>
        </aside>
        <div className="min-w-0 flex-1">
          <div className="mb-4 flex gap-2 overflow-x-auto lg:hidden">
            {visibleTabs.map((t) => (
              <button key={t.key} onClick={() => setTab(t.key)} className={`shrink-0 rounded-full px-4 py-2 text-sm ${tab === t.key ? "bg-olive text-ivory" : "bg-white text-ink/60"}`}>{t.label}</button>
            ))}
          </div>
          {tab === "overview" && <Overview />}
          {tab === "products" && <Products />}
          {tab === "orders" && <Orders />}
          {tab === "users" && <UsersTab isSuper={user.role === "super_admin"} />}
          {tab === "reviews" && <Reviews />}
          {tab === "religious" && <Religious />}
          {tab === "fields" && <Fields />}
          {tab === "settings" && <SettingsTab />}
          {tab === "admins" && user.role === "super_admin" && <Admins />}
          {tab === "audit" && <Audit />}
        </div>
      </div>
    </div>
  );
}

const RANGES = [
  { key: "all", label: "كل الفترات" },
  { key: "today", label: "اليوم" },
  { key: "week", label: "هذا الأسبوع" },
  { key: "month", label: "هذا الشهر" },
  { key: "custom", label: "فترة مخصصة" },
];

function rangeParams(range, from, to) {
  const now = new Date();
  const iso = (d) => d.toISOString().slice(0, 10);
  if (range === "today") return { date_from: iso(now), date_to: iso(now) };
  if (range === "week") { const d = new Date(now); d.setDate(d.getDate() - 7); return { date_from: iso(d), date_to: iso(now) }; }
  if (range === "month") { const d = new Date(now); d.setDate(d.getDate() - 30); return { date_from: iso(d), date_to: iso(now) }; }
  if (range === "custom" && from && to) return { date_from: from, date_to: to };
  return {};
}

function Overview() {
  const [stats, setStats] = useState(null);
  const [range, setRange] = useState("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [notifs, setNotifs] = useState(null);

  const load = () => {
    setStats(null);
    api.get("/admin/stats/full", { params: rangeParams(range, from, to) }).then((r) => setStats(r.data)).catch((e) => toast.error(apiError(e.response?.data?.detail)));
    api.get("/admin/notifications").then((r) => setNotifs(r.data)).catch(() => {});
  };
  useEffect(load, [range, from, to]);

  if (!stats) return <Loading />;
  const bs = stats.by_status || {};
  const cards = [
    { label: "إجمالي الطلبات", value: stats.orders_total },
    { label: "طلبات جديدة", value: bs.new || 0 },
    { label: "قيد التجهيز", value: bs.preparing || 0 },
    { label: "قيد التوصيل", value: bs.delivering || 0 },
    { label: "مكتملة", value: bs.delivered || 0 },
    { label: "ملغية", value: bs.cancelled || 0 },
    { label: "العملاء", value: stats.customers },
    { label: "المنتجات", value: stats.products },
    { label: "المبيعات (مدفوعة)", value: fmtPrice(stats.revenue) },
  ];
  return (
    <>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <H title="لوحة المعلومات" />
        <div className="flex flex-wrap items-center gap-2">
          {RANGES.map((r) => (
            <button key={r.key} data-testid={`range-${r.key}`} onClick={() => setRange(r.key)}
              className={`rounded-full px-3.5 py-1.5 text-xs font-medium transition-colors ${range === r.key ? "bg-olive text-ivory" : "bg-white text-ink/60 border border-olive/15"}`}>
              {r.label}
            </button>
          ))}
          {range === "custom" && (
            <>
              <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="rounded-full border border-olive/20 px-3 py-1.5 text-xs" />
              <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="rounded-full border border-olive/20 px-3 py-1.5 text-xs" />
            </>
          )}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
        {cards.map((c) => (
          <div key={c.label} className="rounded-2xl border border-olive/10 bg-white p-5">
            <div className="font-display text-3xl font-bold text-olive-dark">{c.value}</div>
            <div className="mt-1 text-sm text-ink/55">{c.label}</div>
          </div>
        ))}
      </div>
      {notifs && notifs.items.length > 0 && (
        <div className="mt-6 rounded-2xl border border-olive/10 bg-white p-5">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="flex items-center gap-2 font-display font-bold text-ink"><Bell className="h-4 w-4 text-olive" /> الإشعارات {notifs.unread > 0 && <span className="rounded-full bg-palred px-2 py-0.5 text-xs text-white">{notifs.unread}</span>}</h3>
            <button onClick={async () => { await api.post("/admin/notifications/read"); load(); }} className="text-xs text-olive link-underline">تحديد الكل كمقروء</button>
          </div>
          <div className="space-y-1.5">
            {notifs.items.slice(0, 6).map((n) => (
              <div key={n.id} className={`flex items-center justify-between rounded-xl px-3.5 py-2 text-sm ${n.read ? "text-ink/50" : "bg-olive/[0.05] text-ink"}`}>
                <span>{n.text}</span>
                <span className="text-xs text-ink/40">{new Date(n.created_at).toLocaleString("ar")}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}

const EMPTY_PRODUCT = { sku: "", name_ar: "", name_en: "", category: "olive_oil", country: "PS", geo_source: "nablus_mt", price: 0, weight_grams: 0, image_main: "", short_story: "", natural: true, organic: false, is_demo: true };

// Product-type categories & authentic terrain sources (geography is a tag, not a section)
const ADMIN_CATS = [["olive_oil", "زيت الزيتون"], ["olives_pickles", "الزيتون والمخللات"], ["zaatar_spices", "الزعتر والدقة والتوابل"], ["honey_jams", "العسل والمربيات الطبيعية"], ["soap_care", "الصابون ومنتجات العناية"]];
const ADMIN_GEO = [["nablus_mt", "جبال نابلس", "mountain"], ["jerusalem_mt", "جبال القدس", "mountain"], ["hebron_mt", "جبال الخليل", "mountain"], ["ramallah_mt", "جبال رام الله", "mountain"], ["salfit_mt", "جبال سلفيت", "mountain"], ["tulkarm_hills", "تلال طولكرم", "hill"], ["jenin_hills", "تلال جنين", "hill"]];

function Products() {
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [importing, setImporting] = useState(false);
  const load = () => api.get("/admin/products").then((r) => setProducts(r.data));
  useEffect(() => { load(); }, []);

  const downloadTemplate = () => {
    const header = "sku,name_ar,name_en,category,geo_source,price,weight_grams,image_main,short_story,allergens,natural,organic";
    const sample = "PS-NEW-01,منتج جديد,New Product,olive_oil,nablus_mt,20,500,https://...,حكاية قصيرة,سمسم,نعم,1";
    const blob = new Blob(["\ufeff" + header + "\n" + sample], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob); a.download = "suhool_products_template.csv"; a.click();
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post("/admin/products/bulk", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`أُضيف ${r.data.created} منتج، وتُخطّي ${r.data.skipped}`);
      if (r.data.errors?.length) toast.error(`أخطاء: ${r.data.errors.length}`);
      load();
    } catch (er) { toast.error(apiError(er.response?.data?.detail)); }
    setImporting(false); e.target.value = "";
  };

  const save = async (form) => {
    const geo = ADMIN_GEO.find((g) => g[0] === form.geo_source) || ADMIN_GEO[0];
    const payload = {
      ...form, price: Number(form.price), weight_grams: Number(form.weight_grams),
      country: "PS", geo_source: geo[0], geo_source_ar: geo[1], geo_terrain: geo[2], region: geo[1],
      images: { main: form.image_main },
      fingerprint: { country: "PS", region: geo[1], geo_key: geo[0], terrain: geo[2] },
    };
    delete payload.image_main;
    try {
      if (form._new) { await api.post("/admin/products", payload); toast.success("أُضيف المنتج"); }
      else { await api.put(`/admin/products/${form.sku}`, payload); toast.success("حُدّث المنتج"); }
      setEditing(null); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const del = async (sku) => { if (!window.confirm("حذف المنتج؟")) return; await api.delete(`/admin/products/${sku}`); toast.success("حُذف"); load(); };

  if (editing) return <ProductForm initial={editing} onSave={save} onCancel={() => setEditing(null)} />;

  return (
    <>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <H title={`المنتجات (${products.length})`} />
        <div className="flex flex-wrap gap-2">
          <button onClick={downloadTemplate} className="rounded-full border border-olive/20 px-4 py-2 text-sm font-medium text-olive">تحميل نموذج CSV</button>
          <label data-testid="admin-bulk-import" className="cursor-pointer rounded-full border border-olive/20 px-4 py-2 text-sm font-medium text-olive">
            {importing ? "جارِ الاستيراد..." : "استيراد بالجملة"}
            <input type="file" accept=".csv,.xlsx,.xls" onChange={onUpload} className="hidden" disabled={importing} />
          </label>
          <button data-testid="admin-add-product" onClick={() => setEditing({ ...EMPTY_PRODUCT, _new: true })} className="flex items-center gap-1.5 rounded-full bg-olive px-4 py-2 text-sm font-medium text-ivory"><Plus className="h-4 w-4" /> منتج جديد</button>
        </div>
      </div>
      <div className="space-y-2">
        {products.map((p) => (
          <div key={p.sku} data-testid={`admin-product-${p.sku}`} className="flex items-center gap-3 rounded-2xl border border-olive/10 bg-white p-3">
            {p.images?.main ? (
              <img src={p.images.main} alt="" className="h-12 w-12 rounded-lg object-cover" />
            ) : (
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-olive/10 text-olive/40"><Package className="h-5 w-5" /></div>
            )}
            <div className="flex-1 min-w-0">
              <div className="truncate font-medium text-ink">{p.name_ar}</div>
              <div className="text-xs text-ink/50">{p.sku} · {p.geo_source_ar || p.region} · {fmtPrice(p.price)}</div>
            </div>
            <button onClick={() => setEditing({ ...p, image_main: p.images?.main || "", _new: false })} className="rounded-lg p-2 text-ink/50 hover:text-olive"><Edit className="h-4 w-4" /></button>
            <button data-testid={`admin-delete-${p.sku}`} onClick={() => del(p.sku)} className="rounded-lg p-2 text-ink/50 hover:text-palred"><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
    </>
  );
}

function ProductForm({ initial, onSave, onCancel }) {
  const [f, setF] = useState(initial);
  const set = (k, v) => setF({ ...f, [k]: v });
  const CATS = ADMIN_CATS;
  const GEO = ADMIN_GEO.map((g) => [g[0], g[1]]);
  return (
    <div className="rounded-3xl border border-olive/10 bg-white p-6">
      <button onClick={onCancel} className="mb-4 flex items-center gap-1.5 text-sm text-olive"><ArrowLeft className="h-4 w-4" /> رجوع</button>
      <H title={f._new ? "منتج جديد" : `تعديل ${f.name_ar}`} />
      <div className="grid gap-4 sm:grid-cols-2">
        <In label="الرمز (SKU)" testid="pf-sku" value={f.sku} onChange={(v) => set("sku", v)} disabled={!f._new} />
        <In label="الاسم بالعربية" testid="pf-name" value={f.name_ar} onChange={(v) => set("name_ar", v)} />
        <In label="الاسم بالإنجليزية" value={f.name_en} onChange={(v) => set("name_en", v)} />
        <Sel label="التصنيف (نوع المنتج)" value={f.category} onChange={(v) => set("category", v)} options={CATS} />
        <Sel label="المصدر الجغرافي (التضاريس)" value={f.geo_source} onChange={(v) => set("geo_source", v)} options={GEO} />
        <In label="السعر ($)" type="number" testid="pf-price" value={f.price} onChange={(v) => set("price", v)} />
        <In label="الوزن (غم)" type="number" value={f.weight_grams} onChange={(v) => set("weight_grams", v)} />
        <div className="sm:col-span-2"><In label="رابط الصورة الرئيسية" value={f.image_main} onChange={(v) => set("image_main", v)} /></div>
        <div className="sm:col-span-2">
          <label className="mb-1.5 block text-sm text-ink/60">حكاية المنتج</label>
          <textarea value={f.short_story} onChange={(e) => set("short_story", e.target.value)} rows={2} className="w-full rounded-xl border border-olive/20 px-4 py-3 outline-none" />
        </div>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.natural} onChange={(e) => set("natural", e.target.checked)} className="accent-olive" /> طبيعي</label>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.organic} onChange={(e) => set("organic", e.target.checked)} className="accent-olive" /> عضوي موثّق</label>
      </div>
      <button data-testid="pf-save" onClick={() => onSave(f)} className="mt-6 rounded-full bg-olive px-8 py-3 font-semibold text-ivory">حفظ</button>
    </div>
  );
}

function Orders() {
  const [orders, setOrders] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [detail, setDetail] = useState(null);
  const [qr, setQr] = useState(null);

  const load = () => {
    const params = {};
    if (q.trim()) params.q = q.trim();
    if (status) params.status = status;
    if (from) params.date_from = from;
    if (to) params.date_to = to;
    api.get("/admin/orders", { params }).then((r) => setOrders(r.data)).catch((e) => toast.error(apiError(e.response?.data?.detail)));
  };
  useEffect(() => { load(); }, [status]);

  const openDetail = async (order_no) => {
    const r = await api.get(`/admin/orders/${order_no}/detail`);
    setDetail(r.data);
    setQr(null);
    api.get(`/admin/orders/${order_no}/qr`).then((x) => setQr(x.data)).catch(() => {});
  };

  const setFulfillment = async (order_no, s) => {
    try {
      await api.post(`/admin/orders/${order_no}/fulfillment`, { status: s });
      toast.success("حُدّثت الحالة وأُشعر العميل");
      setDetail((d) => d && { ...d, fulfillment_status: s });
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const downloadQr = () => {
    if (!qr) return;
    const a = document.createElement("a");
    a.href = qr.image; a.download = `QR-${detail.order_no}.png`; a.click();
  };

  return (
    <>
      <H title={`الطلبات (${orders.length})`} />
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="flex min-w-52 flex-1 items-center gap-2 rounded-full border border-olive/20 bg-white px-4">
          <Search className="h-4 w-4 shrink-0 text-olive" />
          <input data-testid="orders-search" value={q} onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="رقم الطلب، اسم العميل، الهاتف..." className="w-full bg-transparent py-2.5 text-sm outline-none" />
        </div>
        <select data-testid="orders-status-filter" value={status} onChange={(e) => setStatus(e.target.value)} className="rounded-full border border-olive/20 bg-white px-4 py-2.5 text-sm">
          <option value="">كل الحالات</option>
          {Object.entries(FUL).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <input type="date" data-testid="orders-from" value={from} onChange={(e) => setFrom(e.target.value)} className="rounded-full border border-olive/20 bg-white px-3 py-2.5 text-sm" />
        <input type="date" data-testid="orders-to" value={to} onChange={(e) => setTo(e.target.value)} className="rounded-full border border-olive/20 bg-white px-3 py-2.5 text-sm" />
        <button data-testid="orders-apply" onClick={load} className="rounded-full bg-olive px-5 py-2.5 text-sm font-semibold text-ivory">بحث</button>
      </div>

      {orders.length === 0 && <Empty text="لا توجد طلبات مطابقة." />}
      <div className="space-y-2">
        {orders.map((o) => (
          <div key={o.order_no} data-testid={`admin-order-${o.order_no}`} className="rounded-2xl border border-olive/10 bg-white p-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="min-w-0">
                <span className="font-display font-semibold text-ink">{o.order_no}</span>
                <span className="text-sm text-ink/50"> · {o.name} · </span>
                <span className="text-sm text-ink/50" dir="ltr">{o.phone}</span>
                <span className="text-sm font-semibold text-olive-dark"> · {fmtPrice(o.total)}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className={`rounded-full px-3 py-1 text-xs font-semibold ${FUL[o.fulfillment_status || "new"].cls}`}>{FUL[o.fulfillment_status || "new"].label}</span>
                <button data-testid={`order-detail-${o.order_no}`} onClick={() => openDetail(o.order_no)}
                  className="flex items-center gap-1 rounded-full border border-olive/20 px-3.5 py-1.5 text-xs font-medium text-olive-dark transition-colors hover:bg-olive/5">
                  <Eye className="h-3.5 w-3.5" /> تفاصيل
                </button>
              </div>
            </div>
            <div className="mt-1 text-xs text-ink/45">{o.items.map((i) => `${i.name_ar}×${i.qty}`).join("، ")} · {new Date(o.created_at).toLocaleString("ar")}</div>
          </div>
        ))}
      </div>

      {detail && (
        <div className="fixed inset-0 z-[80] flex items-end justify-center bg-olive-deep/45 backdrop-blur-sm sm:items-center" onClick={() => setDetail(null)}>
          <div data-testid="order-detail-modal" onClick={(e) => e.stopPropagation()}
            className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-t-3xl bg-ivory p-6 shadow-2xl sm:rounded-3xl">
            <div className="mb-5 flex items-center justify-between">
              <h3 className="font-display text-xl font-bold text-ink">الطلب {detail.order_no}</h3>
              <button onClick={() => setDetail(null)} className="flex h-9 w-9 items-center justify-center rounded-full bg-olive/5 text-ink/60"><X className="h-5 w-5" /></button>
            </div>

            <div className="grid grid-cols-2 gap-3 text-sm">
              <Meta label="العميل" value={detail.name} />
              <Meta label="الهاتف" value={detail.phone} />
              <Meta label="المدينة" value={`${detail.city}${detail.area ? "، " + detail.area : ""}`} />
              <Meta label="التاريخ" value={new Date(detail.created_at).toLocaleString("ar")} />
              <div className="col-span-2"><Meta label="العنوان" value={[detail.address, detail.building && `بناء ${detail.building}`, detail.street].filter(Boolean).join("، ")} /></div>
              {detail.note && <div className="col-span-2"><Meta label="ملاحظات" value={detail.note} /></div>}
            </div>

            <div className="mt-4 space-y-1.5">
              {detail.items.map((i) => (
                <div key={i.sku} className="flex justify-between rounded-xl border border-olive/10 bg-white px-4 py-2.5 text-sm">
                  <span>{i.name_ar} × {i.qty} <span className="text-ink/45">({fmtPrice(i.price)})</span></span>
                  <span className="font-semibold text-olive-dark">{fmtPrice(i.price * i.qty)}</span>
                </div>
              ))}
              <div className="flex justify-between px-4 py-2 text-sm text-ink/60"><span>الشحن</span><span>{fmtPrice(detail.shipping)}</span></div>
              {detail.discount > 0 && <div className="flex justify-between px-4 py-1 text-sm text-palred"><span>الخصم</span><span>-{fmtPrice(detail.discount)}</span></div>}
              <div className="flex justify-between px-4 py-2 font-display text-lg font-bold text-olive-dark"><span>الإجمالي</span><span>{fmtPrice(detail.total)}</span></div>
            </div>

            <div className="mt-5 grid gap-4 sm:grid-cols-[1fr_auto]">
              <div>
                <label className="mb-1.5 block text-sm text-ink/60">حالة الطلب</label>
                <select data-testid="fulfillment-select" value={detail.fulfillment_status || "new"} onChange={(e) => setFulfillment(detail.order_no, e.target.value)}
                  className="w-full rounded-xl border border-olive/20 bg-white px-4 py-3 text-sm outline-none">
                  {Object.entries(FUL).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
                <div className="mt-2 text-xs text-ink/45">الدفع: {detail.status === "paid" ? "مدفوع" : detail.payment_method === "cod" ? "عند الاستلام" : "بانتظار الدفع"}</div>
              </div>
              <div className="text-center">
                {qr ? (
                  <>
                    <img src={qr.image} alt="QR" className="mx-auto h-32 w-32 rounded-xl border border-olive/10" data-testid="order-qr-image" />
                    <div className="mt-2 flex justify-center gap-2">
                      <button data-testid="qr-download" onClick={downloadQr} className="flex items-center gap-1 rounded-full border border-olive/20 px-3 py-1.5 text-xs text-olive-dark"><Download className="h-3.5 w-3.5" /> تحميل</button>
                      <Link data-testid="qr-print" to={`/admin/print/${detail.order_no}`} target="_blank" className="flex items-center gap-1 rounded-full bg-olive px-3 py-1.5 text-xs font-semibold text-ivory"><Printer className="h-3.5 w-3.5" /> طباعة بطاقة الطرد</Link>
                    </div>
                  </>
                ) : (
                  <div className="flex h-32 w-32 items-center justify-center rounded-xl bg-olive/5"><QrCode className="h-8 w-8 animate-pulse text-olive/40" /></div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function UsersTab({ isSuper }) {
  const [users, setUsers] = useState([]);
  const [detail, setDetail] = useState(null);
  const load = () => api.get("/admin/users").then((r) => setUsers(r.data));
  useEffect(() => { load(); }, []);
  const setRole = async (id, role) => {
    try { await api.post(`/admin/users/${id}/role`, { role }); toast.success("حُدّث الدور"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const openDetail = async (id) => {
    try { const r = await api.get(`/admin/customers/${id}`); setDetail(r.data); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  return (
    <>
      <H title={`العملاء (${users.length})`} />
      <div className="space-y-2">
        {users.map((u) => (
          <div key={u.id} className="flex items-center justify-between gap-2 rounded-2xl border border-olive/10 bg-white p-4">
            <button onClick={() => openDetail(u.id)} className="min-w-0 flex-1 text-right">
              <div className="truncate font-medium text-ink">{u.name}</div>
              <div className="truncate text-xs text-ink/50">{u.email}</div>
            </button>
            {isSuper ? (
              <select data-testid={`user-role-${u.email}`} value={u.role} onChange={(e) => setRole(u.id, e.target.value)} className="rounded-full border border-olive/20 px-3 py-1.5 text-sm">
                <option value="user">مستخدم</option><option value="manager">مدير طلبات</option><option value="admin">مشرف</option>
                {u.role === "super_admin" && <option value="super_admin">مدير عام</option>}
              </select>
            ) : (
              <span className="rounded-full bg-olive/8 px-3 py-1 text-xs text-olive-dark">{u.role === "user" || !u.role ? "مستخدم" : u.role}</span>
            )}
          </div>
        ))}
      </div>

      {detail && (
        <div className="fixed inset-0 z-[80] flex items-end justify-center bg-olive-deep/45 backdrop-blur-sm sm:items-center" onClick={() => setDetail(null)}>
          <div onClick={(e) => e.stopPropagation()} className="max-h-[85vh] w-full max-w-xl overflow-y-auto rounded-t-3xl bg-ivory p-6 sm:rounded-3xl">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="font-display text-xl font-bold text-ink">{detail.user.name}</h3>
              <button onClick={() => setDetail(null)} className="flex h-9 w-9 items-center justify-center rounded-full bg-olive/5 text-ink/60"><X className="h-5 w-5" /></button>
            </div>
            <div className="text-sm text-ink/60">{detail.user.email}</div>
            <h4 className="mt-5 mb-2 font-display font-bold text-ink">طلباته ({detail.orders.length})</h4>
            <div className="space-y-1.5">
              {detail.orders.map((o) => (
                <div key={o.order_no} className="flex justify-between rounded-xl border border-olive/10 bg-white px-4 py-2.5 text-sm">
                  <span>{o.order_no} · {FUL[o.fulfillment_status || "new"].label}</span>
                  <span className="font-semibold text-olive-dark">{fmtPrice(o.total)}</span>
                </div>
              ))}
              {detail.orders.length === 0 && <p className="text-sm text-ink/45">لا توجد طلبات.</p>}
            </div>
            <h4 className="mt-5 mb-2 font-display font-bold text-ink">عناوينه ({detail.addresses.length})</h4>
            <div className="space-y-1.5">
              {detail.addresses.map((a) => (
                <div key={a.id} className="rounded-xl border border-olive/10 bg-white px-4 py-2.5 text-sm">{a.label} — {a.city}، {a.address}</div>
              ))}
              {detail.addresses.length === 0 && <p className="text-sm text-ink/45">لا توجد عناوين محفوظة.</p>}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function Reviews() {
  const [reviews, setReviews] = useState([]);
  const load = () => api.get("/admin/reviews").then((r) => setReviews(r.data));
  useEffect(() => { load(); }, []);
  const moderate = async (id, status) => { await api.post(`/admin/reviews/${id}/moderate`, { status }); toast.success("تم"); load(); };
  return (
    <>
      <H title={`التقييمات (${reviews.length})`} />
      {reviews.length === 0 && <Empty text="لا توجد تقييمات." />}
      <div className="space-y-2">
        {reviews.map((r) => (
          <div key={r.id} className="rounded-2xl border border-olive/10 bg-white p-4">
            <div className="flex items-center justify-between">
              <div className="font-medium text-ink">{r.user_name} · {r.product_sku} · ⭐{r.rating}</div>
              <span className={`rounded-full px-2 py-0.5 text-xs ${r.status === "approved" ? "bg-olive/10 text-olive" : r.status === "rejected" ? "bg-palred/10 text-palred" : "bg-earth/10 text-earth"}`}>{r.status}</span>
            </div>
            {r.comment && <p className="mt-1 text-sm text-ink/65">{r.comment}</p>}
            {r.status === "pending" && (
              <div className="mt-3 flex gap-2">
                <button data-testid={`review-approve-${r.id}`} onClick={() => moderate(r.id, "approved")} className="flex items-center gap-1 rounded-full bg-olive px-4 py-1.5 text-sm text-ivory"><Check className="h-3.5 w-3.5" /> اعتماد</button>
                <button onClick={() => moderate(r.id, "rejected")} className="flex items-center gap-1 rounded-full border border-palred/30 px-4 py-1.5 text-sm text-palred"><X className="h-3.5 w-3.5" /> رفض</button>
              </div>
            )}
          </div>
        ))}
      </div>
    </>
  );
}

const EMPTY_REL = { product_sku: "", text: "", quote: "", source: "", reference: "", hadith_grade: "", link: "", note: "" };
const STAGE_AR = { draft: "مسودة", review: "قيد المراجعة", approved: "معتمد" };

function Religious() {
  const [list, setList] = useState([]);
  const [form, setForm] = useState(null);
  const load = () => api.get("/admin/religious").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);
  const create = async () => {
    try { await api.post("/admin/religious", form); toast.success("أُنشئت المسودة"); setForm(null); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const action = async (id, act) => {
    try { await api.post(`/admin/religious/${id}/${act}`); toast.success("تم"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  return (
    <>
      <div className="mb-4 flex items-center justify-between">
        <H title="المحتوى الديني" />
        {!form && <button data-testid="rel-new" onClick={() => setForm({ ...EMPTY_REL })} className="flex items-center gap-1.5 rounded-full bg-olive px-4 py-2 text-sm font-medium text-ivory"><Plus className="h-4 w-4" /> محتوى جديد</button>}
      </div>
      <div className="mb-4 rounded-xl bg-olive/5 p-3 text-sm text-ink/60">المسار: مسودة ← مراجعة ← اعتماد. لا يمكن للمُدخِل اعتماد ما أدخله بنفسه — المراجع يجب أن يكون شخصاً آخر. لا يظهر للزوار إلا بعد الاعتماد.</div>
      {form && (
        <div className="mb-6 rounded-3xl border border-olive/10 bg-white p-5">
          <div className="grid gap-3 sm:grid-cols-2">
            <In label="رمز المنتج (SKU)" testid="rel-sku" value={form.product_sku} onChange={(v) => setForm({ ...form, product_sku: v })} />
            <In label="المصدر" testid="rel-source" value={form.source} onChange={(v) => setForm({ ...form, source: v })} />
            <In label="المرجع" testid="rel-reference" value={form.reference} onChange={(v) => setForm({ ...form, reference: v })} />
            <In label="درجة الحديث (إن وُجد)" value={form.hadith_grade} onChange={(v) => setForm({ ...form, hadith_grade: v })} />
            <div className="sm:col-span-2"><In label="النص/الاقتباس" value={form.quote} onChange={(v) => setForm({ ...form, quote: v })} /></div>
            <div className="sm:col-span-2">
              <label className="mb-1.5 block text-sm text-ink/60">الشرح</label>
              <textarea data-testid="rel-text" value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} rows={2} className="w-full rounded-xl border border-olive/20 px-4 py-3 outline-none" />
            </div>
            <div className="sm:col-span-2"><In label="رابط المصدر" value={form.link} onChange={(v) => setForm({ ...form, link: v })} /></div>
          </div>
          <div className="mt-4 flex gap-2">
            <button data-testid="rel-save" onClick={create} className="rounded-full bg-olive px-6 py-2.5 font-semibold text-ivory">حفظ كمسودة</button>
            <button onClick={() => setForm(null)} className="rounded-full border border-olive/20 px-6 py-2.5 text-ink/60">إلغاء</button>
          </div>
        </div>
      )}
      <div className="space-y-2">
        {list.map((r) => (
          <div key={r.id} data-testid={`rel-item-${r.id}`} className="rounded-2xl border border-olive/10 bg-white p-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="font-medium text-ink">{r.product_sku} — {r.source} ({r.reference})</div>
              <span className={`rounded-full px-2.5 py-0.5 text-xs ${r.status === "approved" ? "bg-olive/10 text-olive" : r.status === "review" ? "bg-earth/10 text-earth" : "bg-ink/5 text-ink/50"}`}>{STAGE_AR[r.status]}</span>
            </div>
            {r.quote && <p className="mt-1 font-display text-olive-dark">{r.quote}</p>}
            <p className="text-sm text-ink/60">{r.text}</p>
            <div className="mt-1 text-xs text-ink/40">أدخله: {r.created_by_email}{r.reviewed_by_email ? ` · اعتمده: ${r.reviewed_by_email}` : ""}</div>
            <div className="mt-3 flex gap-2">
              {r.status === "draft" && <button data-testid={`rel-submit-${r.id}`} onClick={() => action(r.id, "submit")} className="rounded-full bg-earth px-4 py-1.5 text-sm text-ivory">إرسال للمراجعة</button>}
              {r.status === "review" && <>
                <button data-testid={`rel-approve-${r.id}`} onClick={() => action(r.id, "approve")} className="rounded-full bg-olive px-4 py-1.5 text-sm text-ivory">اعتماد</button>
                <button onClick={() => action(r.id, "reject")} className="rounded-full border border-palred/30 px-4 py-1.5 text-sm text-palred">إرجاع لمسودة</button>
              </>}
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

const FIELD_TYPES = [["text", "نص"], ["number", "رقم"], ["select", "قائمة"], ["multiselect", "اختيار متعدد"], ["boolean", "نعم/لا"], ["date", "تاريخ"]];
const EMPTY_FIELD = { key: "", label: "", type: "text", scope: "orders", required: false, active: true, order: 0, optionsText: "" };

function Fields() {
  const [fields, setFields] = useState([]);
  const [form, setForm] = useState(null);
  const load = () => api.get("/admin/fields").then((r) => setFields(r.data)).catch((e) => toast.error(apiError(e.response?.data?.detail)));
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      await api.post("/admin/fields", {
        key: form.key, label: form.label, type: form.type, scope: form.scope,
        required: form.required, active: form.active, order: Number(form.order) || 0,
        options: form.optionsText ? form.optionsText.split("،").map((s) => s.trim()).filter(Boolean) : [],
      });
      toast.success("أُضيف الحقل"); setForm(null); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const toggle = async (f) => { await api.put(`/admin/fields/${f.id}`, { active: !f.active }); load(); };
  const del = async (f) => { if (!window.confirm(`حذف الحقل «${f.label}»؟ يمكنك تعطيله بدلًا من ذلك.`)) return; await api.delete(`/admin/fields/${f.id}`); toast.success("حُذف"); load(); };

  return (
    <>
      <div className="mb-4 flex items-center justify-between">
        <H title="الحقول الديناميكية" />
        {!form && <button data-testid="field-new" onClick={() => setForm({ ...EMPTY_FIELD })} className="flex items-center gap-1.5 rounded-full bg-olive px-4 py-2 text-sm font-medium text-ivory"><Plus className="h-4 w-4" /> إضافة حقل</button>}
      </div>
      <p className="mb-4 rounded-xl bg-olive/5 p-3 text-sm text-ink/60">حقول إضافية تظهر في صفحة إتمام الطلب (نطاق: الطلبات) وتُحفظ مع كل طلب.</p>
      {form && (
        <div className="mb-6 rounded-3xl border border-olive/10 bg-white p-5">
          <div className="grid gap-3 sm:grid-cols-2">
            <In label="مفتاح الحقل (إنجليزي)" testid="field-key" value={form.key} onChange={(v) => setForm({ ...form, key: v })} />
            <In label="الاسم الظاهر" testid="field-label" value={form.label} onChange={(v) => setForm({ ...form, label: v })} />
            <Sel label="نوع الحقل" value={form.type} onChange={(v) => setForm({ ...form, type: v })} options={FIELD_TYPES} />
            <In label="الترتيب" type="number" value={form.order} onChange={(v) => setForm({ ...form, order: v })} />
            {(form.type === "select" || form.type === "multiselect") && (
              <div className="sm:col-span-2"><In label="الخيارات (افصل بـ ،)" value={form.optionsText} onChange={(v) => setForm({ ...form, optionsText: v })} /></div>
            )}
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.required} onChange={(e) => setForm({ ...form, required: e.target.checked })} className="accent-olive" /> مطلوب</label>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="accent-olive" /> فعّال</label>
          </div>
          <div className="mt-4 flex gap-2">
            <button data-testid="field-save" onClick={create} className="rounded-full bg-olive px-6 py-2.5 font-semibold text-ivory">حفظ</button>
            <button onClick={() => setForm(null)} className="rounded-full border border-olive/20 px-6 py-2.5 text-ink/60">إلغاء</button>
          </div>
        </div>
      )}
      <div className="space-y-2">
        {fields.map((f) => (
          <div key={f.id} data-testid={`field-${f.key}`} className="flex flex-wrap items-center justify-between gap-2 rounded-2xl border border-olive/10 bg-white p-4">
            <div>
              <div className="font-medium text-ink">{f.label} <span className="text-xs text-ink/40">({f.key} · {f.scope})</span></div>
              <div className="text-xs text-ink/50">{FIELD_TYPES.find(([v]) => v === f.type)?.[1]} {f.required ? "· مطلوب" : ""} {f.active ? "· فعّال" : "· معطّل"}</div>
            </div>
            <div className="flex gap-2">
              <button onClick={() => toggle(f)} className={`rounded-full px-4 py-1.5 text-xs font-medium ${f.active ? "border border-earth/30 text-earth" : "bg-olive text-ivory"}`}>{f.active ? "تعطيل" : "تفعيل"}</button>
              <button onClick={() => del(f)} className="rounded-full border border-palred/30 px-4 py-1.5 text-xs text-palred">حذف</button>
            </div>
          </div>
        ))}
        {fields.length === 0 && <Empty text="لا توجد حقول مخصصة بعد." />}
      </div>
    </>
  );
}

function SettingsTab() {
  const [value, setValue] = useState("");
  const [guide, setGuide] = useState(null);
  const [assistant, setAssistant] = useState(null);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    Promise.all([
      api.get("/settings/thank_you").then((r) => setValue(r.data.value)),
      api.get("/settings/guide").then((r) => setGuide(r.data)),
      api.get("/settings/assistant").then((r) => setAssistant(r.data)),
    ]).finally(() => setLoaded(true));
  }, []);
  const save = async () => {
    try { await api.put("/admin/settings/thank_you", { value }); toast.success("حُفظت رسالة الشكر"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const saveGuide = async () => {
    try { await api.put("/admin/settings/guide", guide); toast.success("حُفظت إعدادات الدليل"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  if (!loaded) return <Loading />;
  return (
    <>
      <H title="الإعدادات" />
      <div className="space-y-6">
        <div className="rounded-3xl border border-olive/10 bg-white p-6">
          <label className="mb-1.5 block text-sm font-medium text-ink/70">رسالة الشكر في الفاتورة الإلكترونية</label>
          <textarea data-testid="thankyou-input" value={value} onChange={(e) => setValue(e.target.value)} rows={5}
            className="w-full rounded-xl border border-olive/20 px-4 py-3 outline-none focus:border-olive" />
          <button data-testid="thankyou-save" onClick={save} className="mt-4 rounded-full bg-olive px-8 py-3 font-semibold text-ivory">حفظ</button>
        </div>

        {guide && (
          <div className="rounded-3xl border border-olive/10 bg-white p-6">
            <h3 className="mb-4 font-display text-lg font-bold text-ink">الدليل الغذائي (PDF)</h3>
            <div className="space-y-4">
              <div>
                <label className="mb-1.5 block text-sm text-ink/60">عنوان الدليل</label>
                <input data-testid="guide-title" value={guide.title} onChange={(e) => setGuide({ ...guide, title: e.target.value })}
                  className="w-full rounded-xl border border-olive/20 px-4 py-3 outline-none focus:border-olive" />
              </div>
              <div>
                <label className="mb-1.5 block text-sm text-ink/60">رسالة الغلاف</label>
                <textarea data-testid="guide-cover" value={guide.cover} onChange={(e) => setGuide({ ...guide, cover: e.target.value })} rows={2}
                  className="w-full rounded-xl border border-olive/20 px-4 py-3 outline-none focus:border-olive" />
              </div>
              <div>
                <label className="mb-1.5 block text-sm text-ink/60">الرسالة الختامية</label>
                <textarea data-testid="guide-closing" value={guide.closing} onChange={(e) => setGuide({ ...guide, closing: e.target.value })} rows={3}
                  className="w-full rounded-xl border border-olive/20 px-4 py-3 outline-none focus:border-olive" />
              </div>
              <div>
                <label className="mb-1.5 block text-sm text-ink/60">نص التنبيه العام (أسفل الدليل)</label>
                <textarea data-testid="guide-disclaimer" value={guide.disclaimer} onChange={(e) => setGuide({ ...guide, disclaimer: e.target.value })} rows={3}
                  className="w-full rounded-xl border border-olive/20 px-4 py-3 outline-none focus:border-olive" />
              </div>
            </div>
            <button data-testid="guide-save" onClick={saveGuide} className="mt-4 rounded-full bg-olive px-8 py-3 font-semibold text-ivory">حفظ إعدادات الدليل</button>
          </div>
        )}

        {assistant && (
          <div className="rounded-3xl border border-olive/10 bg-white p-6">
            <h3 className="mb-1 font-display text-lg font-bold text-ink">نموذج الذكاء الاصطناعي</h3>
            <p className="mb-4 text-sm text-ink/55">النموذج المستخدم في «رفيق سهول» وتوليد الجداول الغذائية ووصفات «ماذا أطبخ؟».</p>
            <div className="flex flex-wrap items-center gap-3">
              <select data-testid="assistant-model" value={assistant.model}
                onChange={async (e) => {
                  try {
                    await api.put("/admin/settings/assistant_model", { value: e.target.value });
                    setAssistant({ ...assistant, model: e.target.value });
                    toast.success("حُدّث النموذج");
                  } catch (er) { toast.error(apiError(er.response?.data?.detail)); }
                }}
                className="rounded-xl border border-olive/20 bg-white px-4 py-3 text-sm outline-none">
                {assistant.options.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
              </select>
              <span className="rounded-full bg-olive/8 px-3 py-1.5 text-xs font-medium text-olive-dark">الحالي: {assistant.options.find((o) => o.key === assistant.model)?.label}</span>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

function Admins() {
  const [admins, setAdmins] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", password: "", confirm: "", role: "manager", current_password: "" });
  const load = () => api.get("/admin/admins").then((r) => setAdmins(r.data)).catch((e) => toast.error(apiError(e.response?.data?.detail)));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.name || !form.email || !form.password) { toast.error("أكمل الحقول"); return; }
    if (form.password !== form.confirm) { toast.error("كلمتا المرور غير متطابقتين"); return; }
    try {
      await api.post("/admin/admins", { name: form.name, email: form.email, password: form.password, role: form.role, current_password: form.current_password });
      toast.success("أُنشئ حساب المسؤول");
      setForm({ name: "", email: "", password: "", confirm: "", role: "manager", current_password: "" });
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const ROLE_AR = { super_admin: "مدير عام", admin: "مشرف", manager: "مدير طلبات" };
  return (
    <>
      <H title="المديرون والصلاحيات" />
      <div className="mb-6 space-y-2">
        {admins.map((a) => (
          <div key={a.id} className="flex items-center justify-between rounded-2xl border border-olive/10 bg-white p-4">
            <div><div className="font-medium text-ink">{a.name}</div><div className="text-xs text-ink/50">{a.email}</div></div>
            <span className="rounded-full bg-olive/10 px-3 py-1 text-xs font-semibold text-olive-dark">{ROLE_AR[a.role] || a.role}</span>
          </div>
        ))}
      </div>

      <div className="rounded-3xl border border-olive/10 bg-white p-6">
        <h3 className="mb-1 font-display text-lg font-bold text-ink">إنشاء مسؤول جديد</h3>
        <p className="mb-4 flex items-center gap-1.5 rounded-xl bg-gold/10 p-3 text-xs text-olive-dark"><ShieldCheck className="h-4 w-4 shrink-0" /> يتطلب إنشاء مسؤول جديد إعادة تأكيد هويتك بكلمة مرورك الحالية — لا يكفي أن يكون المتصفح مسجلًا.</p>
        <div className="grid gap-3 sm:grid-cols-2">
          <In label="اسم المسؤول" testid="admin-new-name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
          <In label="البريد الإلكتروني" testid="admin-new-email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
          <In label="كلمة المرور" type="password" testid="admin-new-password" value={form.password} onChange={(v) => setForm({ ...form, password: v })} />
          <In label="تأكيد كلمة المرور" type="password" value={form.confirm} onChange={(v) => setForm({ ...form, confirm: v })} />
          <Sel label="مستوى الصلاحية" value={form.role} onChange={(v) => setForm({ ...form, role: v })} options={[["manager", "مدير طلبات — الطلبات والعملاء فقط"], ["admin", "مشرف — كل شيء عدا إدارة المديرين"]]} />
          <In label="كلمة مرورك الحالية (تأكيد الهوية)" type="password" testid="admin-current-password" value={form.current_password} onChange={(v) => setForm({ ...form, current_password: v })} />
        </div>
        <button data-testid="admin-create" onClick={create} className="mt-5 rounded-full bg-olive px-8 py-3 font-semibold text-ivory">إنشاء المسؤول</button>
      </div>
    </>
  );
}

function Audit() {
  const [log, setLog] = useState([]);
  useEffect(() => { api.get("/admin/audit").then((r) => setLog(r.data)).catch(() => {}); }, []);
  return (
    <>
      <H title="سجل العمليات" />
      <p className="mb-4 rounded-xl bg-olive/5 p-3 text-sm text-ink/60">يسجَّل كل إجراء إداري حساس تلقائيًا. لا يمكن حذف هذا السجل من الواجهة.</p>
      <div className="space-y-1.5">
        {log.map((l) => (
          <div key={l.id} className="flex items-center justify-between rounded-xl border border-olive/10 bg-white px-4 py-2.5 text-sm">
            <span className="text-ink/75"><b className="text-olive-dark">{l.actor}</b> — {l.action} <span className="text-ink/40">{l.target}</span></span>
            <span className="text-xs text-ink/40">{new Date(l.at).toLocaleString("ar")}</span>
          </div>
        ))}
      </div>
    </>
  );
}

const Meta = ({ label, value }) => (
  <div className="rounded-xl bg-olive/[0.04] px-3.5 py-2.5">
    <div className="text-[11px] text-ink/45">{label}</div>
    <div className="mt-0.5 font-medium text-ink">{value || "—"}</div>
  </div>
);

const H = ({ title }) => <h2 className="mb-4 font-display text-2xl font-bold text-ink">{title}</h2>;
const Loading = () => <div className="py-16 text-center text-ink/50">جارِ التحميل...</div>;
const Empty = ({ text }) => <div className="rounded-2xl border border-dashed border-olive/20 py-12 text-center text-ink/50">{text}</div>;
const In = ({ label, value, onChange, type = "text", disabled, testid }) => (
  <div>
    <label className="mb-1.5 block text-sm text-ink/60">{label}</label>
    <input data-testid={testid} type={type} value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)}
      className="w-full rounded-xl border border-olive/20 px-4 py-2.5 outline-none focus:border-olive disabled:bg-ink/5" />
  </div>
);
const Sel = ({ label, value, onChange, options }) => (
  <div>
    <label className="mb-1.5 block text-sm text-ink/60">{label}</label>
    <select value={value} onChange={(e) => onChange(e.target.value)} className="w-full rounded-xl border border-olive/20 px-4 py-2.5 outline-none">
      {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  </div>
);
